import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateTimeShort, formatTimeHHMM } from '../lib/format'
import type { TcccVitals, VitalsKey } from '../lib/tccc'
import { useTheme } from '../lib/theme'

/**
 * Chrome colours for the SVG chart. Keyed by theme so the plot, grid, and
 * axis labels read cleanly against both light (stone-50) and dark
 * (stone-950) page backgrounds. Series accents are kept as-is — those are
 * brand signals (pulse=red, BP=teal, etc.) and stay the same in both
 * themes.
 */
const CHROME = {
  light: {
    plotFill: '#fafaf9', // stone-50
    plotStroke: '#e7e5e4', // stone-200
    grid: '#f5f5f4', // stone-100
    tick: '#a8a29e', // stone-400
    tickLabel: '#57534e', // stone-600
    axisTitle: '#78716c', // stone-500
  },
  dark: {
    plotFill: '#1c1917', // stone-900
    plotStroke: '#44403c', // stone-700
    grid: '#292524', // stone-800
    tick: '#78716c', // stone-500
    tickLabel: '#d6d3d1', // stone-300
    axisTitle: '#a8a29e', // stone-400
  },
}

type TFn = ReturnType<typeof useTranslation>['t']

interface SeriesSpec {
  key: VitalsKey
  /** i18n key under `vitals.*` used for the legend/axis label. */
  labelKey: string
  unit: string
  accent: string
  /** Healthy range, shown as a band when focused. */
  healthy?: { lo: number; hi: number }
  /** Axis range used for normalization. Falls back to the data's own min/max. */
  yClamp?: { lo: number; hi: number }
  decimals?: number
}

const SERIES: SeriesSpec[] = [
  {
    key: 'pulse',
    labelKey: 'vitals.pulse',
    unit: 'bpm',
    accent: '#b91c1c',
    healthy: { lo: 60, hi: 100 },
    yClamp: { lo: 40, hi: 180 },
  },
  {
    key: 'systolicBP',
    labelKey: 'vitals.systolicBp',
    unit: 'mmHg',
    accent: '#0f766e',
    healthy: { lo: 90, hi: 140 },
    yClamp: { lo: 60, hi: 180 },
  },
  {
    key: 'diastolicBP',
    labelKey: 'vitals.diastolicBp',
    unit: 'mmHg',
    accent: '#047857',
    healthy: { lo: 60, hi: 90 },
    yClamp: { lo: 30, hi: 120 },
  },
  {
    key: 'respirationRate',
    labelKey: 'vitals.respiration',
    unit: '/min',
    accent: '#1d4ed8',
    healthy: { lo: 12, hi: 20 },
    yClamp: { lo: 6, hi: 40 },
  },
  {
    key: 'oxygenSaturation',
    labelKey: 'vitals.spo2',
    unit: '%',
    accent: '#7c3aed',
    healthy: { lo: 94, hi: 100 },
    yClamp: { lo: 70, hi: 100 },
  },
]

interface VitalsChartProps {
  series: TcccVitals[]
}

interface PreparedSeries {
  spec: SeriesSpec
  points: { t: number; v: number }[]
  dataMin: number
  dataMax: number
  yLo: number
  yHi: number
  latest: number
}

/**
 * Single combined chart of a casualty's vitals time series. Every enabled
 * metric is plotted on a shared normalized y-axis (0 at each metric's lower
 * clamp, 1 at its upper clamp) so the lines stay legible despite very
 * different units. The legend doubles as a filter: hovering or focusing
 * an entry shows only that metric's line and its healthy band, with the
 * real-units y-axis labelled on the side.
 */
export function VitalsChart({ series }: VitalsChartProps) {
  const { t } = useTranslation()
  const { resolved } = useTheme()
  const chrome = CHROME[resolved]
  const [focusKey, setFocusKey] = useState<VitalsKey | null>(null)

  // Sort numerically by parsed timestamp. String compare is wrong here: a
  // payload may contain both `…T18:04Z` (zero-second shorthand) and
  // `…T18:04:55Z`, and `Z` > `:` lexicographically would place the shorthand
  // entry after every `…:NNZ` sibling, producing zigzag lines on the chart.
  const sorted = useMemo(
    () => [...series].sort((a, b) => Date.parse(a.time) - Date.parse(b.time)),
    [series],
  )

  const prepared = useMemo(() => prepareSeries(sorted), [sorted])

  if (sorted.length === 0) {
    return <p className="text-sm text-stone-500 dark:text-stone-400">{t('vitals.noVitals')}</p>
  }
  if (prepared.length === 0) {
    return (
      <p className="text-sm text-stone-500 dark:text-stone-400">{t('vitals.noNumericVitals')}</p>
    )
  }

  const firstMs = Date.parse(sorted[0]!.time)
  const lastMs = Date.parse(sorted[sorted.length - 1]!.time)
  const totalMs = Math.max(lastMs - firstMs, 1)
  const ticks = computeTimeTicks(firstMs, lastMs)
  const multiDay = spansMultipleDays(firstMs, lastMs)

  // Chart geometry. Width is viewBox-relative; the SVG scales to container.
  const W = 960
  const H = 260
  const PAD_L = focusKey ? 52 : 40
  const PAD_R = 16
  const PAD_T = 12
  const PAD_B = 32
  const plotW = W - PAD_L - PAD_R
  const plotH = H - PAD_T - PAD_B
  const xFor = (t: number) => PAD_L + ((t - firstMs) / totalMs) * plotW
  // Normalized y: fraction → pixel (0 at bottom, 1 at top of plot area).
  const yForNorm = (n: number) => PAD_T + plotH - n * plotH

  const focused = focusKey ? prepared.find((p) => p.spec.key === focusKey) : null

  return (
    <div>
      <Legend
        prepared={prepared}
        focusKey={focusKey}
        onFocus={setFocusKey}
        onBlur={() => setFocusKey(null)}
        t={t}
      />
      <div className="mt-3 overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label="Vitals trend over time"
          className="block"
        >
          <rect
            x={PAD_L}
            y={PAD_T}
            width={plotW}
            height={plotH}
            fill={chrome.plotFill}
            stroke={chrome.plotStroke}
          />
          {/* Gridlines at 25/50/75% of the normalized range. */}
          {[0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1={PAD_L}
              x2={PAD_L + plotW}
              y1={yForNorm(f)}
              y2={yForNorm(f)}
              stroke={chrome.grid}
            />
          ))}

          {/* Focused series: healthy band + real-units y-axis. */}
          {focused?.spec.healthy ? (
            <FocusedBand series={focused} plotW={plotW} padL={PAD_L} yForNorm={yForNorm} />
          ) : null}
          {focused ? (
            <YAxis
              series={focused}
              padL={PAD_L}
              padT={PAD_T}
              plotH={plotH}
              yForNorm={yForNorm}
              t={t}
            />
          ) : null}

          {/* Time-axis ticks. */}
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={xFor(tick)}
                x2={xFor(tick)}
                y1={PAD_T + plotH}
                y2={PAD_T + plotH + 4}
                stroke={chrome.tick}
              />
              <text
                x={xFor(tick)}
                y={PAD_T + plotH + 16}
                textAnchor="middle"
                fontSize="10"
                fill={chrome.tickLabel}
              >
                {formatTick(tick, multiDay)}
              </text>
            </g>
          ))}
          {/* x-axis title under the ticks. */}
          <text
            x={PAD_L + plotW / 2}
            y={H - 4}
            textAnchor="middle"
            fontSize="10"
            fill={chrome.axisTitle}
          >
            {t('vitals.timeAxis')}
          </text>

          {/* Lines. The focused series draws last so it sits on top. */}
          {prepared
            .filter((p) => !focusKey || p.spec.key === focusKey)
            .sort((a, b) =>
              focusKey ? (a.spec.key === focusKey ? 1 : b.spec.key === focusKey ? -1 : 0) : 0,
            )
            .map((p) => (
              <SeriesPath
                key={p.spec.key}
                series={p}
                dimmed={false}
                xFor={xFor}
                yForNorm={yForNorm}
              />
            ))}
          {!focusKey
            ? null
            : prepared
                .filter((p) => p.spec.key !== focusKey)
                .map((p) => (
                  <SeriesPath key={p.spec.key} series={p} dimmed xFor={xFor} yForNorm={yForNorm} />
                ))}
        </svg>
      </div>
      <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
        {t('vitals.readings', {
          count: sorted.length,
          first: formatAxisTime(firstMs),
          last: formatAxisTime(lastMs),
        })}
        {focused ? (
          <>
            {'. '}
            {t('vitals.focused', {
              label: t(focused.spec.labelKey),
              range: focused.spec.healthy
                ? `${formatN(focused.spec.healthy.lo, focused.spec.decimals)}–${formatN(
                    focused.spec.healthy.hi,
                    focused.spec.decimals,
                  )}${focused.spec.unit}`
                : t('vitals.healthyUnspecified'),
            })}
          </>
        ) : (
          `. ${t('vitals.hint')}`
        )}
      </p>
    </div>
  )
}

interface LegendProps {
  prepared: PreparedSeries[]
  focusKey: VitalsKey | null
  onFocus: (k: VitalsKey) => void
  onBlur: () => void
  t: TFn
}

function Legend({ prepared, focusKey, onFocus, onBlur, t }: LegendProps) {
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1" role="list">
      {prepared.map((p) => {
        const active = focusKey === p.spec.key
        const dimmed = focusKey !== null && !active
        return (
          <li key={p.spec.key}>
            <button
              type="button"
              className={`group inline-flex items-baseline gap-1.5 rounded px-1.5 py-0.5 text-xs transition-opacity ${
                dimmed ? 'opacity-40' : ''
              } ${active ? 'bg-stone-100 dark:bg-stone-800' : 'hover:bg-stone-50 dark:hover:bg-stone-800'}`}
              onMouseEnter={() => {
                onFocus(p.spec.key)
              }}
              onMouseLeave={onBlur}
              onFocus={() => {
                onFocus(p.spec.key)
              }}
              onBlur={onBlur}
            >
              <span
                aria-hidden
                className="inline-block h-0.5 w-4 rounded"
                style={{ backgroundColor: p.spec.accent }}
              />
              <span className="font-medium text-stone-700 dark:text-stone-200">
                {t(p.spec.labelKey)}
              </span>
              <span className="tabular-nums text-stone-500 dark:text-stone-400">
                {formatN(p.latest, p.spec.decimals)}
                {p.spec.unit}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function SeriesPath({
  series,
  dimmed,
  xFor,
  yForNorm,
}: {
  series: PreparedSeries
  dimmed: boolean
  xFor: (t: number) => number
  yForNorm: (n: number) => number
}) {
  const { spec, points, yLo, yHi } = series
  const span = Math.max(yHi - yLo, 1e-9)
  const d = points
    .map((p, i) => {
      const n = (p.v - yLo) / span
      return `${i === 0 ? 'M' : 'L'}${xFor(p.t).toFixed(1)} ${yForNorm(n).toFixed(1)}`
    })
    .join(' ')
  return (
    <path
      d={d}
      fill="none"
      stroke={spec.accent}
      strokeWidth={dimmed ? 1 : 1.75}
      opacity={dimmed ? 0.2 : 1}
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  )
}

function FocusedBand({
  series,
  plotW,
  padL,
  yForNorm,
}: {
  series: PreparedSeries
  plotW: number
  padL: number
  yForNorm: (n: number) => number
}) {
  const { spec, yLo, yHi } = series
  if (!spec.healthy) return null
  const span = Math.max(yHi - yLo, 1e-9)
  const lo = Math.max(spec.healthy.lo, yLo)
  const hi = Math.min(spec.healthy.hi, yHi)
  if (hi <= lo) return null
  const yTop = yForNorm((hi - yLo) / span)
  const yBot = yForNorm((lo - yLo) / span)
  return (
    <rect
      x={padL}
      y={yTop}
      width={plotW}
      height={Math.max(0, yBot - yTop)}
      fill={hexWithAlpha(spec.accent, 0.08)}
      stroke={hexWithAlpha(spec.accent, 0.25)}
      strokeDasharray="2 2"
    />
  )
}

function YAxis({
  series,
  padL,
  padT,
  plotH,
  yForNorm,
  t,
}: {
  series: PreparedSeries
  padL: number
  padT: number
  plotH: number
  yForNorm: (n: number) => number
  t: TFn
}) {
  const { spec, yLo, yHi } = series
  const span = Math.max(yHi - yLo, 1e-9)
  const ticks = [yLo, yLo + span * 0.25, yLo + span * 0.5, yLo + span * 0.75, yHi]
  return (
    <g>
      <line x1={padL} x2={padL} y1={padT} y2={padT + plotH} stroke={spec.accent} opacity={0.4} />
      {ticks.map((v, i) => {
        const y = yForNorm((v - yLo) / span)
        return (
          <g key={i}>
            <line x1={padL - 3} x2={padL} y1={y} y2={y} stroke={spec.accent} opacity={0.4} />
            <text x={padL - 5} y={y + 3} textAnchor="end" fontSize="10" fill={spec.accent}>
              {formatN(v, spec.decimals)}
            </text>
          </g>
        )
      })}
      <text
        x={padL - 38}
        y={padT + plotH / 2}
        textAnchor="middle"
        fontSize="10"
        fill={spec.accent}
        transform={`rotate(-90 ${padL - 38} ${padT + plotH / 2})`}
      >
        {t(spec.labelKey)}
        {spec.unit ? ` (${spec.unit})` : ''}
      </text>
    </g>
  )
}

function prepareSeries(series: TcccVitals[]): PreparedSeries[] {
  const out: PreparedSeries[] = []
  for (const spec of SERIES) {
    const points: { t: number; v: number }[] = []
    for (const row of series) {
      const v = row[spec.key]
      if (typeof v === 'number' && Number.isFinite(v)) {
        points.push({ t: Date.parse(row.time), v })
      }
    }
    if (points.length === 0) continue
    const values = points.map((p) => p.v)
    const dataMin = Math.min(...values)
    const dataMax = Math.max(...values)
    // yClamp defines a stable axis range so the curve doesn't auto-fit to the
    // data's own tiny variation; a noisy-but-in-range pulse still looks flat
    // against its clamp range. Falls back to the data's own range when no
    // clamp is defined for the metric.
    const yLo = spec.yClamp ? Math.min(spec.yClamp.lo, dataMin) : dataMin
    const yHi = spec.yClamp ? Math.max(spec.yClamp.hi, dataMax) : dataMax
    out.push({
      spec,
      points,
      dataMin,
      dataMax,
      yLo,
      yHi,
      latest: points[points.length - 1]!.v,
    })
  }
  return out
}

function computeTimeTicks(startMs: number, endMs: number): number[] {
  const totalMs = endMs - startMs
  const minute = 60_000
  const hour = 60 * minute
  // Pick an interval that yields roughly 5–8 ticks.
  const step = (() => {
    if (totalMs <= 5 * minute) return 30_000
    if (totalMs <= 15 * minute) return 2 * minute
    if (totalMs <= hour) return 10 * minute
    if (totalMs <= 3 * hour) return 30 * minute
    if (totalMs <= 12 * hour) return hour
    return 3 * hour
  })()
  // Snap the first tick up to the next step boundary so label times are round.
  const first = Math.ceil(startMs / step) * step
  const ticks: number[] = []
  for (let t = first; t <= endMs; t += step) ticks.push(t)
  // Always include the two endpoints so the axis feels anchored.
  if (ticks.length === 0 || ticks[0]! - startMs > step / 2) ticks.unshift(startMs)
  if (endMs - ticks[ticks.length - 1]! > step / 2) ticks.push(endMs)
  return ticks
}

function spansMultipleDays(startMs: number, endMs: number): boolean {
  return new Date(startMs).toDateString() !== new Date(endMs).toDateString()
}

function formatTick(ms: number, withDate: boolean): string {
  const d = new Date(ms)
  const hhmm = formatTimeHHMM(d)
  if (!withDate) return hhmm
  const mon = d.toLocaleDateString('uk-UA', { month: 'short', day: 'numeric' })
  return `${mon} ${hhmm}`
}

function formatAxisTime(ms: number): string {
  return formatDateTimeShort(new Date(ms))
}

function formatN(n: number, decimals?: number): string {
  return n.toFixed(decimals ?? 0)
}

function hexWithAlpha(hex: string, alpha: number): string {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0')
  return `${hex}${a}`
}
